/**
 * Centralized validation utilities for form inputs.
 */

/**
 * Strips everything except digits and leading +
 */
export const sanitizePhone = (value) => {
  const cleaned = value.replace(/[^\d+\s()-]/g, '');
  return cleaned;
};

/**
 * Validates a phone number: must be 7-15 digits (ignoring formatting chars).
 * Allows optional leading +, spaces, dashes, parentheses.
 */
export const isValidPhone = (phone) => {
  if (!phone || !phone.trim()) return false;
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
};

/**
 * Validates an email address.
 */
export const isValidEmail = (email) => {
  if (!email || !email.trim()) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
};

/**
 * Validates a non-empty trimmed string with optional min length.
 */
export const isValidName = (name, minLength = 2) => {
  if (!name || !name.trim()) return false;
  return name.trim().length >= minLength;
};

/**
 * Returns a human-readable error message, or null if valid.
 */
export const validateField = (field, value) => {
  switch (field) {
    case 'name':
      if (!value || !value.trim()) return 'Name is required';
      if (value.trim().length < 2) return 'Name must be at least 2 characters';
      return null;
    case 'email':
      if (!value || !value.trim()) return 'Email is required';
      if (!isValidEmail(value)) return 'Please enter a valid email address';
      return null;
    case 'phone':
      if (!value || !value.trim()) return 'Phone number is required';
      if (!isValidPhone(value)) return 'Please enter a valid phone number (7-15 digits)';
      return null;
    case 'subject':
      if (!value || !value.trim()) return 'Subject is required';
      return null;
    case 'message':
      if (!value || !value.trim()) return 'Message is required';
      return null;
    default:
      return null;
  }
};

/**
 * Validate multiple fields at once.
 * @param {Object} fields - { fieldName: value }
 * @returns {Object} - { fieldName: errorMessage | null }
 */
export const validateForm = (fields) => {
  const errors = {};
  let hasErrors = false;
  Object.entries(fields).forEach(([field, value]) => {
    const error = validateField(field, value);
    if (error) {
      errors[field] = error;
      hasErrors = true;
    }
  });
  return { errors, hasErrors };
};
